'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GameEmbedProps {
  thumbnail: string;
  thumbnailAlt: string;
  title: string;
  isPlaying: boolean;
  onPlay: () => void;
  onStop: () => void;
}

export default function GameEmbed({ thumbnail, thumbnailAlt, title, isPlaying, onPlay, onStop }: GameEmbedProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  return (
    <div className={`rounded-2xl overflow-hidden border border-border bg-card ${isFullscreen ? 'fixed inset-4 z-50 flex flex-col' : ''}`}>
      {/* Controls */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2">
          <button
            onClick={isPlaying ? onStop : onPlay}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 ${
              isPlaying ? 'bg-danger/15 text-danger hover:bg-danger/25' : 'gradient-primary text-white hover:opacity-90'
            }`}
          >
            <Icon name={isPlaying ? 'StopIcon' : 'PlayIcon'} size={15} variant="solid" />
            {isPlaying ? 'Parar' : 'Jogar'}
          </button>
          {isPlaying && (
            <div className="flex items-center gap-1.5 text-xs text-success">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Em execução
            </div>
          )}
        </div>
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          title="Ecrã completo"
        >
          <Icon name={isFullscreen ? 'ArrowsPointingInIcon' : 'ArrowsPointingOutIcon'} size={16} />
        </button>
      </div>

      {/* Game area */}
      <div className={`relative bg-[#050508] ${isFullscreen ? 'flex-1' : 'aspect-video'}`}>
        {!isPlaying ? (
          <>
            <AppImage
              src={thumbnail}
              alt={thumbnailAlt}
              fill
              sizes="(max-width: 1280px) 100vw, 66vw"
              className="object-cover opacity-40"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <button
                onClick={onPlay}
                className="w-20 h-20 rounded-full gradient-primary flex items-center justify-center glow-primary hover:opacity-90 active:scale-95 transition-all duration-200 mb-4"
              >
                <Icon name="PlayIcon" size={36} className="text-white ml-2" variant="solid" />
              </button>
              <p className="text-white font-semibold text-lg">{title}</p>
              <p className="text-white/60 text-sm mt-1">Clica para começar a jogar</p>
            </div>
          </>
        ) : (
          /* Simulated game canvas when playing */
          <div className="absolute inset-0 gradient-hero grid-pattern">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-3 animate-float glow-primary">
                <Icon name="SparklesIcon" size={28} className="text-white" variant="solid" />
              </div>
              <p className="text-white/80 text-sm font-medium">Jogo em execução</p>
              <p className="text-white/40 text-xs mt-1">Preview simulado — integração real via iframe</p>
            </div>
            {/* Simulated HUD */}
            <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
              <span className="text-xs font-mono text-white/70 bg-black/40 px-2 py-1 rounded">HP: ████░ 80%</span>
              <span className="text-xs font-mono text-white/70 bg-black/40 px-2 py-1 rounded tabular-nums">Nível 3 · XP: 240/500</span>
              <span className="text-xs font-mono text-white/70 bg-black/40 px-2 py-1 rounded tabular-nums">Ouro: 150</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}